import { useContext, useRef } from "react";
import { Container, interfaces } from "inversify";
import { InversifyContext } from "@presentation/components/dependencyInjection/ContainerProvider";

type ResolvedDependency = {
  container: Container;
  identifier: interfaces.ServiceIdentifier<unknown>;
  instance: unknown;
};

/**
 * Defines the hook used to resolve a dependency in a component.
 *
 * The resolved instance is cached for the lifetime of the component and only re-resolved if the
 * container or the identifier actually changes.
 *
 * Most services are bound in transient scope, so resolving on every render would hand back a new
 * instance each time. Any component placing an injected service in a hook dependency array (a
 * `useCallback`, `useMemo` or `useEffect`) would then see that dependency change on every render,
 * which is enough to drive an unbounded render/fetch loop.
 *
 * A ref is used rather than `useMemo` deliberately: `useMemo` is a performance hint that React is
 * free to discard, whereas the stable identity relied on here is a correctness requirement.
 */
export function useInjection<T>(identifier: interfaces.ServiceIdentifier<T>): T {
  const { container } = useContext(InversifyContext);
  const resolvedReference = useRef<ResolvedDependency | null>(null);

  if (!container) {
    throw new Error("The container should not be null");
  }

  const cached = resolvedReference.current;
  if (!cached || cached.container !== container || cached.identifier !== identifier) {
    let instance: T;
    try {
      instance = container.get<T>(identifier);
    } catch {
      instance = container.resolve<T>(identifier as interfaces.Newable<T>);
    }

    resolvedReference.current = { container, identifier, instance };
    return instance;
  }

  return cached.instance as T;
}
